/**
 * Medical Record Types
 *
 * TypeScript interfaces for the medical consultation module.
 *
 * @version 2025.1.0
 */

// ============================================================================
// ENUMS
// ============================================================================

/**
 * Smoking status for anamnesis
 */
export enum SmokingStatus {
  NEVER = 'NEVER',
  FORMER = 'FORMER',
  CURRENT = 'CURRENT',
}

/**
 * Alcohol use status for anamnesis
 */
export enum AlcoholUse {
  NEVER = 'NEVER',
  OCCASIONAL = 'OCCASIONAL',
  REGULAR = 'REGULAR',
}

/**
 * Medical opinion for ASO
 */
export enum MedicalOpinion {
  APT = 'APT',
  INAPT = 'INAPT',
  APT_WITH_RESTRICTIONS = 'APT_WITH_RESTRICTIONS',
}

/**
 * Medical record status
 */
export enum MedicalRecordStatus {
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  CANCELLED = 'CANCELLED',
}

// ============================================================================
// INTERFACES
// ============================================================================

/**
 * Triage data from reception
 */
export interface TriageData {
  id: number;
  appointmentId: number;
  bloodPressureSystolic: number | null;
  bloodPressureDiastolic: number | null;
  heartRate: number | null;
  temperature: number | null;
  weight: number | null;
  height: number | null;
  oxygenSaturation: number | null;
  bmi: number | null;
  observations: string | null;
  createdAt: string;
}

/**
 * Employee data in medical record context
 */
export interface MedicalRecordEmployee {
  id: number;
  name: string;
  cpf: string;
  birthDate: string | null;
  company?: {
    id: number;
    legalName: string;
    tradeName?: string;
  };
}

/**
 * Professional (doctor) data
 */
export interface MedicalRecordProfessional {
  id: number;
  name: string;
  email?: string;
  crm?: string;
}

/**
 * Appointment data in medical record context
 */
export interface MedicalRecordAppointment {
  id: number;
  examType: string;
  selectedDate: string;
  hour: string | null;
  minute: string | null;
  queueNumber: number | null;
  attendanceStatus: string;
  triageRecords?: TriageData[];
  clinic?: {
    id: number;
    name: string;
  };
}

/**
 * Medical record entity
 */
export interface MedicalRecord {
  id: number;
  appointmentId: number;
  employeeId: number;
  professionalId: number;

  // Occupational Anamnesis
  chiefComplaint: string | null;
  occupationalHistory: string | null;
  currentJobDescription: string | null;
  previousJobs: string | null;
  workAccidentHistory: string | null;
  medicalHistory: string | null;
  familyHistory: string | null;
  currentMedications: string | null;
  allergies: string | null;

  // Habits
  smokingStatus: SmokingStatus | null;
  smokingDetails: string | null;
  alcoholUse: AlcoholUse | null;
  alcoholDetails: string | null;
  physicalActivity: boolean | null;
  physicalActivityDetails: string | null;

  // Physical Examination
  generalCondition: string | null;
  skinExam: string | null;
  headNeckExam: string | null;
  cardiovascularExam: string | null;
  respiratoryExam: string | null;
  abdominalExam: string | null;
  neurologicalExam: string | null;
  musculoskeletalExam: string | null;
  otherFindings: string | null;

  // ASO Conclusion
  medicalOpinion: MedicalOpinion | null;
  restrictions: string | null;
  recommendations: string | null;
  returnDate: string | null;
  cidCodes: string | null;

  // Metadata
  consultationStartedAt: string;
  consultationCompletedAt: string | null;
  status: MedicalRecordStatus;
  asoPdfPath: string | null;
  asoGeneratedAt: string | null;

  createdAt: string;
  updatedAt: string;

  // Associations
  appointment?: MedicalRecordAppointment;
  employee?: MedicalRecordEmployee;
  professional?: MedicalRecordProfessional;
}

/**
 * Consultation queue item (appointment ready for consultation)
 */
export interface ConsultationQueueItem {
  id: number;
  employeeId: number;
  companyId: number;
  clinicId: number | null;
  examType: string;
  selectedDate: string;
  hour: string | null;
  minute: string | null;
  queueNumber: number | null;
  attendanceStatus: string;
  presenceConfirmedAt: string | null;
  queueCalledAt: string | null;
  employee: MedicalRecordEmployee;
  clinic?: {
    id: number;
    name: string;
  };
  triageRecords?: TriageData[];
  medicalRecord?: MedicalRecord | null;
}

// ============================================================================
// FILTER INTERFACES
// ============================================================================

/**
 * Filters for medical record queries
 */
export interface MedicalRecordFilters {
  employeeId?: number;
  professionalId?: number;
  status?: MedicalRecordStatus | MedicalRecordStatus[];
  medicalOpinion?: MedicalOpinion | MedicalOpinion[];
  startDate?: string;
  endDate?: string;
  companyId?: number;
  clinicId?: number;
}

/**
 * Filters for consultation queue
 */
export interface ConsultationQueueFilters {
  date?: string;
  clinicId?: number;
  companyId?: number;
  attendanceStatus?: string | string[];
  search?: string;
  examType?: string;
}

/**
 * Pagination parameters
 */
export interface PaginationParams {
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'ASC' | 'DESC';
}

// ============================================================================
// DATA TRANSFER INTERFACES
// ============================================================================

/**
 * Data for starting a consultation
 */
export interface StartConsultationData {
  appointmentId: number;
  employeeId: number;
  professionalId: number;
}

/**
 * Data for updating anamnesis
 */
export interface UpdateAnamnesisData {
  chiefComplaint?: string | null;
  occupationalHistory?: string | null;
  currentJobDescription?: string | null;
  previousJobs?: string | null;
  workAccidentHistory?: string | null;
  medicalHistory?: string | null;
  familyHistory?: string | null;
  currentMedications?: string | null;
  allergies?: string | null;
  smokingStatus?: SmokingStatus | null;
  smokingDetails?: string | null;
  alcoholUse?: AlcoholUse | null;
  alcoholDetails?: string | null;
  physicalActivity?: boolean | null;
  physicalActivityDetails?: string | null;
}

/**
 * Data for updating physical exam
 */
export interface UpdatePhysicalExamData {
  generalCondition?: string | null;
  skinExam?: string | null;
  headNeckExam?: string | null;
  cardiovascularExam?: string | null;
  respiratoryExam?: string | null;
  abdominalExam?: string | null;
  neurologicalExam?: string | null;
  musculoskeletalExam?: string | null;
  otherFindings?: string | null;
}

/**
 * Data for completing consultation (ASO)
 */
export interface CompleteConsultationData {
  medicalOpinion: MedicalOpinion;
  restrictions?: string | null;
  recommendations?: string | null;
  returnDate?: string | null;
  cidCodes?: string[] | null;
}

/**
 * Combined update data
 */
export interface UpdateMedicalRecordData
  extends UpdateAnamnesisData,
    UpdatePhysicalExamData,
    Partial<CompleteConsultationData> {}

// ============================================================================
// RESPONSE INTERFACES
// ============================================================================

/**
 * Employee medical history response
 */
export interface EmployeeMedicalHistory {
  employee: MedicalRecordEmployee;
  totalRecords: number;
  records: MedicalRecord[];
}

/**
 * Statistics response
 */
export interface MedicalRecordStatistics {
  byStatus: Record<MedicalRecordStatus, number>;
  byMedicalOpinion: Record<string, number>;
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get medical opinion label in Portuguese
 */
export function getMedicalOpinionLabel(opinion: MedicalOpinion | null): string {
  if (!opinion) return '-';
  const labels: Record<MedicalOpinion, string> = {
    [MedicalOpinion.APT]: 'APTO',
    [MedicalOpinion.INAPT]: 'INAPTO',
    [MedicalOpinion.APT_WITH_RESTRICTIONS]: 'APTO COM RESTRIÇÕES',
  };
  return labels[opinion] || opinion;
}

/**
 * Get smoking status label in Portuguese
 */
export function getSmokingStatusLabel(status: SmokingStatus | null): string {
  if (!status) return '-';
  const labels: Record<SmokingStatus, string> = {
    [SmokingStatus.NEVER]: 'Nunca fumou',
    [SmokingStatus.FORMER]: 'Ex-fumante',
    [SmokingStatus.CURRENT]: 'Fumante',
  };
  return labels[status] || status;
}

/**
 * Get alcohol use label in Portuguese
 */
export function getAlcoholUseLabel(use: AlcoholUse | null): string {
  if (!use) return '-';
  const labels: Record<AlcoholUse, string> = {
    [AlcoholUse.NEVER]: 'Não consome',
    [AlcoholUse.OCCASIONAL]: 'Uso ocasional',
    [AlcoholUse.REGULAR]: 'Uso regular',
  };
  return labels[use] || use;
}

/**
 * Get medical record status label in Portuguese
 */
export function getMedicalRecordStatusLabel(status: MedicalRecordStatus): string {
  const labels: Record<MedicalRecordStatus, string> = {
    [MedicalRecordStatus.IN_PROGRESS]: 'Em Andamento',
    [MedicalRecordStatus.COMPLETED]: 'Concluído',
    [MedicalRecordStatus.CANCELLED]: 'Cancelado',
  };
  return labels[status] || status;
}

/**
 * Get medical opinion color for display
 */
export function getMedicalOpinionColor(
  opinion: MedicalOpinion | null
): 'success' | 'error' | 'warning' | 'default' {
  if (!opinion) return 'default';
  const colors: Record<MedicalOpinion, 'success' | 'error' | 'warning'> = {
    [MedicalOpinion.APT]: 'success',
    [MedicalOpinion.INAPT]: 'error',
    [MedicalOpinion.APT_WITH_RESTRICTIONS]: 'warning',
  };
  return colors[opinion] || 'default';
}

/**
 * Get status color for display
 */
export function getMedicalRecordStatusColor(
  status: MedicalRecordStatus
): 'info' | 'success' | 'error' | 'default' {
  const colors: Record<MedicalRecordStatus, 'info' | 'success' | 'error'> = {
    [MedicalRecordStatus.IN_PROGRESS]: 'info',
    [MedicalRecordStatus.COMPLETED]: 'success',
    [MedicalRecordStatus.CANCELLED]: 'error',
  };
  return colors[status] || 'default';
}

/**
 * Parse CID codes from JSON string
 */
export function parseCidCodes(cidCodesJson: string | null): string[] {
  if (!cidCodesJson) return [];
  try {
    return JSON.parse(cidCodesJson);
  } catch {
    return [];
  }
}

/**
 * Format blood pressure for display
 */
export function formatBloodPressure(
  systolic: number | null,
  diastolic: number | null
): string {
  if (systolic === null || diastolic === null) return '-';
  return `${systolic}/${diastolic} mmHg`;
}

/**
 * Calculate age from birth date
 */
export function calculateAge(birthDate: string | null): number | null {
  if (!birthDate) return null;
  const birth = new Date(birthDate);
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age--;
  }
  return age;
}
